import { useNavigate } from '@tanstack/react-router'
import { useCallback } from 'react'
import { z } from 'zod'
import { useOidc } from '@/features/oidc/oidc'

export const storeSearchSchema = z.object({
  store: z
    .union([z.literal('play-for-free'), z.literal('buy-gold-coins')])
    .optional(),
})

export type StoreSearchValue = z.infer<typeof storeSearchSchema>['store']

export const useStoreLaunch = () => {
  const navigate = useNavigate()

  const { decodedIdToken } = useOidc({ assert: 'user logged in' })
  const storeTabName = `store-tab-${decodedIdToken['pam-aid']}`

  const close = useCallback(() => {
    return navigate({
      to: '.',
      search: (prev) => ({
        ...prev,
        store: undefined,
      }),
      resetScroll: false,
      replace: true,
    })
  }, [navigate])

  const open = useCallback(() => {
    const storeTab: StoreSearchValue =
      (localStorage.getItem(storeTabName) as StoreSearchValue) ||
      'play-for-free'

    return navigate({
      to: '.',
      search: (prev) => ({
        ...prev,
        store: storeTab,
      }),
      resetScroll: false,
      replace: true,
    })
  }, [navigate, storeTabName])

  const changeTab = useCallback(
    (storeSearchValue: NonNullable<StoreSearchValue>) => {
      localStorage.setItem(storeTabName, storeSearchValue)
      return navigate({
        to: '.',
        search: (prev) => ({
          ...prev,
          store: storeSearchValue,
        }),
        resetScroll: false,
        replace: true,
      })
    },

    [navigate, storeTabName],
  )

  return {
    open,
    close,
    changeTab,
  }
}
